import { useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { MENU } from "../data/menu";
import { GreekFlag } from "./GreekFlag";

const ORDERED_MENU = [...MENU].sort((a, b) => a.no.localeCompare(b.no));

const rowReveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: i * 0.04 } }),
};

const normalizeDecimals = (s) => s.replace(/\$(\d+)\.(\d)(?!\d)/g, "$$$1.$20");
const priceLabel = (p) => {
  if (!p || p === "—") return "MP";
  const s = p.includes("$") ? p : /^\d/.test(p) ? `$${p}` : p;
  return normalizeDecimals(s);
};

const Category = ({ cat, total }) => (
  <div id={`cat-${cat.id}`} data-testid={`menu-cat-${cat.id}`} className="scroll-mt-40 py-14 lg:py-20 border-t border-[var(--hair)]">
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-16">
      <div
        className={`lg:sticky lg:top-40 self-start ${cat.highlight ? "rounded-[4px] p-6 lg:p-7" : ""}`}
        data-testid={cat.highlight ? `menu-highlight-${cat.id}` : undefined}
        style={
          cat.highlight
            ? {
                background: cat.strong ? "var(--navy)" : "var(--ice)",
                borderLeft: `3px solid ${cat.strong ? "var(--azure)" : "var(--azure)"}`,
                boxShadow: cat.strong ? "0 30px 70px -40px rgba(0,42,84,0.6)" : "none",
              }
            : undefined
        }
      >
        {cat.highlight && (
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-4"
            style={{
              background: cat.strong ? "var(--azure)" : "#fff",
              border: cat.strong ? "none" : "1px solid var(--hair)",
            }}
          >
            <Star size={12} fill={cat.strong ? "#fff" : "var(--azure)"} stroke="none" />
            <span
              className="font-mono text-[10px] tracking-[0.22em] uppercase"
              style={{ color: cat.strong ? "#fff" : "var(--navy)" }}
            >
              {cat.highlight}
            </span>
          </div>
        )}
        <span
          className="block font-mono text-xs tracking-[0.2em]"
          style={{ color: cat.strong ? "rgba(255,255,255,0.6)" : "var(--azure)" }}
        >
          {cat.no} / {total}
        </span>
        <h3
          className="font-display text-4xl lg:text-6xl tracking-tight leading-[0.95] mt-3"
          style={{ color: cat.strong ? "#fff" : "var(--navy)" }}
        >
          {cat.title}
        </h3>
        {cat.id === "gyros" && (
          <div className="mt-4 flex items-center gap-2.5" data-testid="greek-flag-accent">
            <GreekFlag className="w-8 h-auto" />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(255,255,255,0.75)" }}>Greek Specialty</span>
          </div>
        )}
        <p
          className="mt-4 italic font-display text-lg lg:text-xl"
          style={{ color: cat.strong ? "rgba(255,255,255,0.8)" : "var(--slate)" }}
        >
          {cat.tagline}
        </p>
      </div>

      <div>
        {cat.items.map((it, i) => (
          <motion.div
            key={it.name}
            variants={rowReveal}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-40px" }}
            className="group flex items-end py-4 border-b border-[var(--hair)]"
          >
            <div className="min-w-0">
              <span className="block text-lg lg:text-xl font-semibold text-[var(--ink)] transition-colors duration-300 group-hover:text-[var(--azure)]">
                {it.name}
              </span>
              {it.desc && <span className="block text-sm text-[var(--slate)] mt-0.5 max-w-md">{it.desc}</span>}
            </div>
            <span className="dotted-leader" />
            <span className="font-mono text-[var(--navy)] text-sm lg:text-base whitespace-nowrap pb-0.5">
              {priceLabel(it.price)}
            </span>
          </motion.div>
        ))}
        {cat.note && (
          <p className="mt-6 text-sm text-[var(--slate)] bg-[var(--ice)] rounded-[2px] px-5 py-4 leading-relaxed">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] block mb-1">Good to know</span>
            {cat.note}
          </p>
        )}
      </div>
    </div>
  </div>
);

export const MenuSection = () => {
  const [active, setActive] = useState(ORDERED_MENU[0].id);

  const go = (id) => {
    setActive(id);
    document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="menu" data-testid="menu-section" className="bg-white">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-24 lg:pt-36">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--slate)]">The Menu</span>
          <span className="h-px flex-1 bg-[var(--hair)]" />
        </div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl text-[var(--navy)] tracking-tighter leading-none">
          Everything, <span className="italic text-[var(--azure)]">golden.</span>
        </h2>
      </div>

      {/* sticky category rail */}
      <div className="sticky top-20 z-30 mt-12" style={{ background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", borderTop: "1px solid var(--hair)", borderBottom: "1px solid var(--hair)" }}>
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="flex gap-2 overflow-x-auto py-4 no-scrollbar">
            {ORDERED_MENU.map((c) => (
              <button
                key={c.id}
                onClick={() => go(c.id)}
                data-testid={`menu-tab-${c.id}`}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-mono tracking-[0.12em] uppercase transition-colors duration-300"
                style={
                  active === c.id
                    ? { background: "var(--navy)", color: "#fff" }
                    : { background: "var(--ice)", color: "var(--slate)" }
                }
              >
                {c.highlight && (
                  <Star size={11} fill={active === c.id ? "#fff" : "var(--azure)"} stroke="none" />
                )}
                {c.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pb-10">
        {ORDERED_MENU.map((cat) => (
          <Category key={cat.id} cat={cat} total={String(ORDERED_MENU.length).padStart(2, "0")} />
        ))}
      </div>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </section>
  );
};
